import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { addMinutes } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { task } from 'ember-concurrency';
import MapSearchQuery from 'garaje/graphql/queries/MapSearchQuery';
import type ApolloService from 'garaje/services/apollo-extension';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';

const CURRENT_STATE_INTERVAL_MINUTES = 5;

function normalizeForSearch(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

// Define types for the search results
interface MapFeatureSearchResult {
  type: string;
  __typename: 'MapFeatureSearchResultItem';
  feature: {
    id: string;
    name: string;
    floor: {
      id: string;
      name: string;
      __typename: 'MapFloor';
    };
    __typename: 'MapPointOfInterestFeature';
  };
}

interface EmployeeSearchResult {
  type: string;
  __typename: 'EmployeeMapSearchResultItem';
  employee: {
    id: number;
    name: string;
    email: string;
    profilePictureUrl?: string;
  };
}

type MapSearchResultItem = MapFeatureSearchResult | EmployeeSearchResult;

// Resource types that should be categorized as "resources"
const RESOURCE_TYPES = ['parking', 'desk', 'room'];

type LocalMapFeatureCandidate = {
  id: string;
  featureType: string | null;
  displayName: string;
  normalizedName: string;
  floorId: string;
  floorName: string;
};

// GraphQL query types
interface MapSearchQueryVariables {
  term: string;
  locationId: string;
  startTime: string;
  endTime: string;
  timeoutMilliseconds?: number;
  includeMapFeatures: boolean;
  includeEmployees: boolean;
  draftId?: string;
}

interface MapSearchQueryType {
  __typename: 'MapSearchResults';
  term: string;
  locationId: string;
  startTime: string;
  endTime: string;
  includeEmployees: boolean;
  includeMapFeatures: boolean;
  draftId?: string;
  items: MapSearchResultItem[];
}

export default class MapSearchService extends Service {
  @service declare apolloExtension: ApolloService;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare router: RouterService;
  @service declare store: Store;

  #localIndexKey: string | null = null;
  #localDeskNamesById: Map<string, string> = new Map();
  #localMapFeatureCandidates: LocalMapFeatureCandidate[] = [];

  // Search results
  @tracked searchResults: MapSearchResultItem[] = [];
  @tracked isSearching = false;
  @tracked shouldClearInput = false;

  _toOfficeLocationTime(date: Date): Date {
    return this.state.getOfficeLocationTime(date);
  }

  _getStartEndTime(): { startTime: string; endTime: string } {
    const startTime = toZonedTime(new Date(), this.state.currentLocation.timezone);
    const endTime = addMinutes(startTime, CURRENT_STATE_INTERVAL_MINUTES);

    return {
      startTime: this._toOfficeLocationTime(startTime).toISOString(),
      endTime: this._toOfficeLocationTime(endTime).toISOString(),
    };
  }

  #buildLocalSearchIndex(): void {
    const desks = this.store.peekAll('desk').slice();
    const mapFeatures = this.store.peekAll('map-feature').slice();
    const mapFloors = this.store.peekAll('map-floor').slice();
    const indexKey = `${desks.length}:${mapFeatures.length}:${mapFloors.length}`;
    if (indexKey === this.#localIndexKey) {
      return;
    }
    this.#localIndexKey = indexKey;

    this.#localDeskNamesById.clear();
    for (const desk of desks) {
      if (desk.id && desk.name) {
        // eslint-disable-next-line ember/use-ember-get-and-set
        this.#localDeskNamesById.set(String(desk.id), desk.name);
      }
    }

    const candidates: LocalMapFeatureCandidate[] = [];
    for (const mapFeature of mapFeatures) {
      /* eslint-disable ember/use-ember-get-and-set */
      const featureType = mapFeature.type;
      const externalId = mapFeature.externalId;
      const featureName = mapFeature.name;
      const displayName =
        featureType === 'desk' && externalId ? this.#localDeskNamesById.get(String(externalId)) : featureName;
      /* eslint-enable ember/use-ember-get-and-set */
      if (!displayName) {
        continue;
      }

      const normalizedName = normalizeForSearch(displayName);
      if (!normalizedName) {
        continue;
      }

      const floorId = mapFeature.belongsTo('mapFloor').id();
      if (!floorId) {
        continue;
      }
      const floor = this.store.peekRecord('map-floor', floorId);
      const floorName = floor?.name ?? '';

      candidates.push({
        id: String(mapFeature.id),
        featureType,
        displayName,
        normalizedName,
        floorId,
        floorName,
      });
    }
    this.#localMapFeatureCandidates = candidates;
  }

  #getLocalMapFeatureMatches(searchTerm: string): MapFeatureSearchResult[] {
    const normalizedQuery = normalizeForSearch(searchTerm);
    const queryTokens = normalizedQuery.split(' ').filter(Boolean);
    if (!queryTokens.length) {
      return [];
    }

    this.#buildLocalSearchIndex();

    const scored: Array<{ score: number; result: MapFeatureSearchResult }> = [];
    for (const candidate of this.#localMapFeatureCandidates) {
      if (!queryTokens.every((t) => candidate.normalizedName.includes(t))) {
        continue;
      }

      const result: MapFeatureSearchResult = {
        type: (candidate.featureType || 'point_of_interest').toUpperCase(),
        __typename: 'MapFeatureSearchResultItem',
        feature: {
          id: candidate.id,
          name: candidate.displayName,
          floor: { id: candidate.floorId, name: candidate.floorName, __typename: 'MapFloor' },
          __typename: 'MapPointOfInterestFeature',
        },
      };

      const score =
        (candidate.normalizedName.includes(normalizedQuery) ? 1000 : 0) +
        Math.max(0, 200 - candidate.normalizedName.length);
      scored.push({ score, result });
    }

    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 30).map((x) => x.result);
  }

  searchMapFeaturesAndEmployees = task({ drop: true }, async (searchTerm: string) => {
    if (this.searchResults.length > 0) {
      this.searchResults = [];
    }

    if (!searchTerm.trim()) {
      this.searchResults = [];
      return;
    }

    this.isSearching = true;

    try {
      const locationId = this.state.currentLocation?.id;
      if (!locationId) {
        throw new Error('No current location available');
      }

      const { startTime, endTime } = this._getStartEndTime();

      const variables: MapSearchQueryVariables = {
        term: searchTerm,
        locationId,
        startTime,
        endTime,
        timeoutMilliseconds: 5000, // 5 second timeout
        includeMapFeatures: true,
        includeEmployees: true,
      };

      const results = (await this.apolloExtension.query({
        query: MapSearchQuery,
        fetchPolicy: 'network-only',
        variables,
      })) as unknown as { mapSearch: MapSearchQueryType };

      if (results?.mapSearch?.items) {
        const backendItems = results.mapSearch.items;
        const backendFeatureIds = new Set(
          backendItems
            .filter((item): item is MapFeatureSearchResult => item.__typename === 'MapFeatureSearchResultItem')
            .map((item) => item.feature.id),
        );

        const localMatches = this.#getLocalMapFeatureMatches(searchTerm).filter(
          (item) => !backendFeatureIds.has(item.feature.id),
        );

        // Put locally-found map features first so specific matches don't get buried by generic backend results.
        this.searchResults = [...localMatches, ...backendItems];
      } else {
        this.searchResults = this.#getLocalMapFeatureMatches(searchTerm);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Map search failed:', error);
      this.flashMessages.showFlash('error', 'Search failed. Please try again.');
      this.searchResults = [];
    } finally {
      this.isSearching = false;
    }
  });

  /**
   * Get resources (parking, desk, rooms) from search results
   * Preserves the original order of results
   */
  getResources(): MapFeatureSearchResult[] {
    return this.searchResults.filter(
      (item): item is MapFeatureSearchResult =>
        item.__typename === 'MapFeatureSearchResultItem' && this.#isResourceType(item.feature.name),
    );
  }

  /**
   * Get points of interest (all other resource types) from search results
   * Preserves the original order of results
   */
  getPointOfInterests(): MapFeatureSearchResult[] {
    return this.searchResults.filter(
      (item): item is MapFeatureSearchResult =>
        item.__typename === 'MapFeatureSearchResultItem' && !this.#isResourceType(item.feature.name),
    );
  }

  /**
   * Get employees from search results
   * Preserves the original order of results
   */
  getEmployees(): EmployeeSearchResult[] {
    return this.searchResults.filter(
      (item): item is EmployeeSearchResult => item.__typename === 'EmployeeMapSearchResultItem',
    );
  }

  /**
   * Check if a feature name indicates it's a resource type
   */
  #isResourceType(featureName: string | undefined): boolean {
    if (!featureName) {
      return false;
    }
    const lowerName = featureName.toLowerCase();
    return RESOURCE_TYPES.some((type) => lowerName.includes(type));
  }

  /**
   * Clear search results
   */
  clearSearchResults(): void {
    this.searchResults = [];
  }

  /**
   * Get all map features (both resources and points of interest)
   */
  getAllMapFeatures(): MapFeatureSearchResult[] {
    return this.searchResults.filter(
      (item): item is MapFeatureSearchResult => item.__typename === 'MapFeatureSearchResultItem',
    );
  }
}
